import React, { useState, useEffect, useCallback } from 'react';
import { http } from '../../lib/http';
import { useAuth } from '../../contexts/AuthContext';
import { todayToronto } from '../../lib/time';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';

import KitchenHeader from './components/KitchenHeader';
import MealCountsPanel from './components/MealCountsPanel';
import PlanBreakdownPanel from './components/PlanBreakdownPanel';
import AddOnsPanel from './components/AddOnsPanel';
import TodaysMenuCard from './components/TodaysMenuCard';
import DietaryAlertsPanel from './components/DietaryAlertsPanel';
import ShoppingListPanel from './components/ShoppingListPanel';

const KitchenDashboard = ({ onSwitchDashboard }) => {
  const { user, logout } = useAuth();

  // Core state
  const [selectedDate, setSelectedDate] = useState(todayToronto());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [shoppingList, setShoppingList] = useState(null);

  // Time tracking
  const [timeLogStatus, setTimeLogStatus] = useState('none');
  const [timerSubmitting, setTimerSubmitting] = useState(false);

  // ── Data Loading ──

  const loadDashboard = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.get(`/kitchen/dashboard-summary?date=${date}`);
      if (res.data.success) {
        setDashboardData(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load kitchen data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadShoppingList = useCallback(async (date) => {
    try {
      const res = await http.get(`/kitchen/shopping-list?date=${date}`);
      if (res.data.success) {
        setShoppingList(res.data.data || null);
      }
    } catch (err) {
      console.error('Failed to load shopping list:', err);
    }
  }, []);

  const loadTimeLog = useCallback(async () => {
    try {
      const res = await http.get('/staff/time-log/today');
      if (res.data.ok && res.data.timeLog) {
        if (res.data.timeLog.endTime) {
          setTimeLogStatus('completed');
        } else {
          setTimeLogStatus('active');
        }
      } else {
        setTimeLogStatus('none');
      }
    } catch (err) {
      console.error('Failed to load time log:', err);
    }
  }, []);

  // Load on mount and when date changes
  useEffect(() => {
    loadDashboard(selectedDate);
    loadShoppingList(selectedDate);
  }, [selectedDate, loadDashboard, loadShoppingList]);

  useEffect(() => {
    loadTimeLog();
  }, [loadTimeLog]);

  // ── Timer handlers ──

  const handleStartTimer = async () => {
    setTimerSubmitting(true);
    try {
      const res = await http.post('/staff/time-log/start', {});
      if (res.data.ok) {
        setTimeLogStatus('active');
      }
    } catch (err) {
      console.error('Failed to start timer:', err);
    } finally {
      setTimerSubmitting(false);
    }
  };

  const handleStopTimer = async () => {
    if (!window.confirm("Stop timer? You won't be able to restart it today.")) return;
    setTimerSubmitting(true);
    try {
      const res = await http.post('/staff/time-log/stop', {});
      if (res.data.ok) {
        setTimeLogStatus('completed');
      }
    } catch (err) {
      console.error('Failed to stop timer:', err);
    } finally {
      setTimerSubmitting(false);
    }
  };

  // ── Render ──

  const d = dashboardData;
  const isServiceDay = d?.isServiceDay !== false;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <KitchenHeader
        user={user}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        onSwitchDashboard={onSwitchDashboard}
        timeLogStatus={timeLogStatus}
        timerSubmitting={timerSubmitting}
        onStartTimer={handleStartTimer}
        onStopTimer={handleStopTimer}
        onLogout={logout}
      />

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Refresh + date display */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            {isServiceDay && d?.day
              ? `${d.day.charAt(0).toUpperCase() + d.day.slice(1)}, ${selectedDate}`
              : selectedDate}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadDashboard(selectedDate); loadShoppingList(selectedDate); }}
            disabled={loading}
            className="border-orange-500 text-orange-600 hover:bg-orange-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Loading */}
        {loading && !d && (
          <div className="flex justify-center py-16">
            <RefreshCw className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        )}

        {/* Not a service day */}
        {!loading && d && !isServiceDay && (
          <div className="text-center py-16 text-gray-500">
            <p className="text-xl font-semibold">No service on weekends</p>
            <p className="text-sm mt-1">Select a weekday to view kitchen data.</p>
          </div>
        )}

        {/* Dashboard content */}
        {d && isServiceDay && (
          <>
            <MealCountsPanel
              mealCounts={d.mealCounts}
              servableCustomerCount={d.servableCustomerCount}
              customersWithoutPreferences={d.customersWithoutPreferences}
            />

            <PlanBreakdownPanel
              planBreakdown={d.planBreakdown}
              totalServableCustomers={d.servableCustomerCount}
            />

            <AddOnsPanel
              addOns={d.addOns}
              totalAddOnItems={d.totalAddOnItems}
            />

            <DietaryAlertsPanel alerts={d.dietaryAlerts} />

            <TodaysMenuCard
              menu={d.menu}
              day={d.day ? d.day.charAt(0).toUpperCase() + d.day.slice(1) : ''}
              isServiceDay={true}
            />

            <ShoppingListPanel
              shoppingList={shoppingList}
              selectedDate={selectedDate}
              onRefresh={() => loadShoppingList(selectedDate)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default KitchenDashboard;
